import { action } from '@ember/object';
import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { IssueType } from 'lumen/models/parity-manager/issue';
import type { Transition } from 'lumen/services/transition';

type RouteQueryParams = {
  issueId: string;
  issueType: IssueType;
  subscriptionId: string;
};

interface IssueUploadModel {
  additionalScreenshot: string | null;
  additionalScreenshotFilename: string | null;
  extranetScreenshot: string | null;
  extranetScreenshotFilename: string | null;
  isLoading: boolean;
  screenshotsUrl: string | null;
}

interface AttachmentsResponse {
  additional_screenshot?: string;
  additional_screenshot_filename?: string;
  extranet_screenshot?: string;
  extranet_screenshot_filename?: string;
  screenshots_url?: string;
}

export default class MarketParityParityManagerIssueuploadRoute extends Route {
  queryParams = {
    subscriptionId: {},
    issueId: {},
    issueType: {},
  };

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare transition: Services['transition'];

  beforeModel(transition: Transition): void {
    if (!this.access.can('market.paritymanager.files.edit')) {
      this.transition.redirectToTab(transition, 'market.parity.parity-manager');
    }
  }

  model(params: RouteQueryParams): IssueUploadModel {
    const model = trackedObject<IssueUploadModel>({
      additionalScreenshot: null,
      additionalScreenshotFilename: null,
      extranetScreenshot: null,
      extranetScreenshotFilename: null,
      isLoading: true,
      screenshotsUrl: null,
    });

    if (
      params.subscriptionId &&
      params.issueId &&
      this.hotelAndMarketService.market?.id
    ) {
      // Request the correct HTML code for the escalation mail preview on init
      void this.ajax
        .request<AttachmentsResponse>(
          `${this.ajax.host}/apigateway/v1/app/paritymanager/get_attachments?market_id=${this.hotelAndMarketService.market?.id}&subscription_id=${params.subscriptionId}&issue_id=${params.issueId}&issue_type=${params.issueType}`,
          {
            method: 'GET',
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
          },
        )
        .then((response) => {
          if (response.additional_screenshot) {
            model.additionalScreenshot = response.additional_screenshot;
            model.additionalScreenshotFilename =
              response.additional_screenshot_filename ?? null;
          }
          if (response.extranet_screenshot) {
            model.extranetScreenshot = response.extranet_screenshot;
            model.extranetScreenshotFilename =
              response.extranet_screenshot_filename ?? null;
          }
          if (response.screenshots_url) {
            model.screenshotsUrl = response.screenshots_url;
          }

          model.isLoading = false;
        })
        .catch((error) => {
          model.isLoading = false;
          this.errorTracking.captureException(error);
          void this.alerting.showErrorAlert();
        });
    } else {
      model.isLoading = false;
      void this.alerting.showErrorAlert();
    }

    return model;
  }

  @action
  refreshRoute(): void {
    this.refresh();
  }
}
